import {
  adapterHead,
  adapterHeadEdge,
  adapterHeadEdgeProperties,
  adapterHeadConnection
} from 'src/constants/typenames'
import { generateType } from 'src/api/models/utils/generateNodeType'
import { types, Instance, getParent, onSnapshot } from 'mobx-state-tree'
import { getGlobalPlateSide } from 'src/utils/getGlobalPlateSide'
import { toGlobalPoint } from 'src/api/models/utils/convertGlobal'
import { Side as SideModel } from 'src/api/models/values'
import { getLookAt } from 'src/api/models/utils/getLookAt'
import { Side, top, bottom, left, right, front, back } from 'src/constants/sides'
import { AdapterPlateEdge } from './AdapterPlate'
import {
  adapterHeadCornerModuleWidth,
  adapterHeadMiddleSlotWidth,
  distanceTolerance,
  Vector2
} from 'src/constants/parameters'
import {
  IAdapterHeadConnection,
  IAdapterPlateEdge,
  IAdapterHeadEdgeProperties,
  IAdapterHead,
  IAdapterHeadEdge
} from './types'

const AdapterHead: IAdapterHead = generateType(adapterHead).props({})

const AdapaterHeadEdgeProperties: IAdapterHeadEdgeProperties = generateType(
  adapterHeadEdgeProperties
)
  .props({
    side: SideModel,
    mountPosition: types.array(types.number),
    bracketPositions: types.array(types.array(types.number))
  })
  .views(self => {
    const bP = self.bracketPositions.map(p => p)
    /** 5. AdapterPlateEdge  4. AdapterPlate  3. AdapterHeadConnection  2. types.array(AdapterHeadEdge)  1. AdapterHeadEdge */
    const adapterPlateEdge = getParent<Instance<ReturnType<typeof AdapterPlateEdge>>>(self, 5) //getParentOfType(self, AdapterPlateEdge)
    const boundings = adapterPlateEdge.properties.boundings

    const globalSide = getGlobalPlateSide({
      parentSide: adapterPlateEdge.properties.side,
      selfSide: self.side
    })
    const globalPosition = toGlobalPoint({
      boundings,
      side: globalSide,
      point: (self.mountPosition as unknown) as [number, number]
    })
    const bracketPositions = (bP as any).map((position: [number, number]) =>
      toGlobalPoint({
        boundings,
        side: globalSide,
        point: position
      })
    )

    /**
     *
     * boundings & side & zero &
     *
     */

    return {
      get globalPosition() {
        return globalPosition
      },
      get globalSide(): Side {
        return globalSide
      },
      get lookAt() {
        return getLookAt(globalSide)
      },
      get screwPosition(): [number, number, number] {
        return globalPosition
      },
      get nutPosition() {
        return globalPosition
      },
      get brackets(): [number, number, number][] {
        return bracketPositions
      }
    }
  })

export const AdapterHeadEdge: IAdapterHeadEdge = generateType(adapterHeadEdge).props({
  node: types.optional(AdapterHead, {} as any),
  properties: AdapaterHeadEdgeProperties
})

type BaseDataPosition = { mountPosition: Vector2; bracketPositions: Vector2[] }

type BaseDataSide = typeof left | typeof right | typeof top | typeof bottom
type BaseData = {
  [key in BaseDataSide]: {
    positions: BaseDataPosition[]
  }
}
type PathGeneratorData = {
  [key in 'width' | 'length']: { amount: number; distance: number }
}

type FeetsData = {
  [key in BaseDataSide]: { [key in 'first' | 'last']: [number, number, number] }
}

/**
 *
 *
 *
 */
const getBaseData = (properties: Instance<IAdapterPlateEdge>['node']['properties']) => {
  const headEdges = [
    { size: { type: 'width', value: properties.width.value }, sides: [top, bottom] },
    { size: { type: 'length', value: properties.length.value }, sides: [right, left] }
  ].reduce<BaseData & PathGeneratorData>((prev, { size, sides }) => {
    const middlePart = size.value - 2 * adapterHeadCornerModuleWidth
    const middleAmount = Math.floor(middlePart / (adapterHeadMiddleSlotWidth + distanceTolerance))
    const totalAmount = middleAmount + 2
    const totalDistance = middlePart - middleAmount * adapterHeadMiddleSlotWidth
    const distance = totalDistance / (middleAmount + 1)
    const partSize = size.value / totalAmount
    const positions = [...Array(totalAmount)].map((_, i, a) => {
      const cornerMountPosition = 2 + 2 + 7.5 // 11.5
      const cornerBracketPosition = 24
      const cornerModuleWidth = 31.5

      const odd = (a.length + 1) % 2 === 0
      if (i === 0) {
        return {
          mountPosition: [cornerMountPosition, properties.thickness.value / 2],
          bracketPositions: [[cornerBracketPosition, 0]]
        }
      } else if (i === a.length - 1) {
        return {
          mountPosition: [size.value - cornerMountPosition, properties.thickness.value / 2],
          bracketPositions: [[size.value - cornerBracketPosition, 0]]
        }
      } else if (odd && i === Math.floor(a.length / 2)) {
        const mountPosition = size.value / 2
        return {
          mountPosition: [mountPosition, properties.thickness.value / 2],
          bracketPositions: [
            [mountPosition - 12.5, 0],
            [mountPosition + 12.5, 0]
          ]
        }
      }

      const middleModuleWidth = 40
      const mountPosition =
        cornerModuleWidth + i * distance + (i - 1) * middleModuleWidth + middleModuleWidth / 2

      return {
        mountPosition: [mountPosition, properties.thickness.value / 2],
        bracketPositions: [
          [mountPosition - 12.5, 0],
          [mountPosition + 12.5, 0]
        ]
      }
    })
    return {
      ...prev,
      [size.type]: { amount: middleAmount, totalAmount, distance },
      ...sides.reduce<Partial<BaseData>>(
        (prev, side) => ({ ...prev, [side]: { positions } }),
        {} as Partial<BaseData>
      )
    }
  }, {} as BaseData & PathGeneratorData)
  return headEdges
}

export const AdapterHeadConnection: IAdapterHeadConnection = generateType(adapterHeadConnection)
  // .props({ edges: types.optional(types.array(AdapterHeadEdge), []) })
  .extend(self => {
    const adapterPlateEdge = getParent<Instance<ReturnType<typeof AdapterPlateEdge>>>(self, 2)

    return {
      views: {
        get pathData() {
          const { width, length } = getBaseData(adapterPlateEdge.node.properties)
          return { width, length }
        },
        get feetsData() {
          const { width, length, ...headData } = getBaseData(adapterPlateEdge.node.properties)
          const d: FeetsData = Object.entries(headData).reduce((prev, [side, { positions }]) => {
            const globalSide = getGlobalPlateSide({
              parentSide: adapterPlateEdge.properties.side,
              selfSide: side as Side
            })
            const first = toGlobalPoint({
              boundings: adapterPlateEdge.properties.boundings,
              side: globalSide,
              point: positions[0].mountPosition
            })
            const last = toGlobalPoint({
              boundings: adapterPlateEdge.properties.boundings,
              side: globalSide,
              point: positions[positions.length - 1].mountPosition
            })

            return { ...prev, [side]: { first, last } }
          }, {}) as FeetsData

          return d
        },
        get edges() {
          const { width, length, ...headData } = getBaseData(adapterPlateEdge.node.properties)

          type HeadEdge = { side: BaseDataSide } & BaseDataPosition

          const headEdges = (Object.entries(headData) as [
            BaseDataSide,
            { positions: BaseDataPosition[] }
          ][]).reduce((prev, curr) => {
            return [
              ...prev,
              ...curr[1].positions.map(position => ({ side: curr[0], ...position } as HeadEdge))
            ]
          }, [] as HeadEdge[])
          return headEdges.map(({ side, mountPosition, bracketPositions }) => {
            const bP = bracketPositions.map(p => p)
            const globalSide = getGlobalPlateSide({
              parentSide: adapterPlateEdge.properties.side,
              selfSide: side
            })
            const globalPosition = toGlobalPoint({
              boundings: adapterPlateEdge.properties.boundings,
              side: globalSide,
              point: (mountPosition as unknown) as [number, number]
            })
            return {
              properties: {
                side: side,
                mountPosition: mountPosition,
                bracketPositions,
                globalSide,
                globalPosition,
                screwPosition: globalPosition,
                nutPosition: globalPosition,
                brackets: (bP as any).map((position: [number, number]) =>
                  toGlobalPoint({
                    boundings: adapterPlateEdge.properties.boundings,
                    side: globalSide,
                    point: position
                  })
                )
              }
            }
          })
        }
      }
    }
  })
